import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Plus } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { FormField } from "@/components/ui/form-field";
import { Label } from "@/components/ui/label";
import { OverlayShell } from "@/components/ui/overlay";
import { SkeletonRows } from "@/components/ui/skeleton";
import { errorCode, notifyError } from "@/lib/error-code";
import { DocumentFieldLabels } from "@/lib/labels/document-fields";

import { SectionCard } from "./section-card";
import { DocumentFieldFormSchemas } from "../-utils/document-field-form";

const onError = notifyError(DocumentFieldLabels.error);

function useInvalidateFields() {
	const queryClient = useQueryClient();

	return () => {
		void queryClient.invalidateQueries({
			queryKey: orpc.documents.fields.list.key(),
		});
	};
}

export function DocumentFieldsSection() {
	const [creating, setCreating] = useState(false);
	const [renaming, setRenaming] = useState<{
		id: number;
		label: string;
	} | null>(null);
	const fields = useQuery(orpc.documents.fields.list.queryOptions());
	const invalidate = useInvalidateFields();

	const remove = useMutation(
		orpc.documents.fields.remove.mutationOptions({
			onSuccess: () => {
				invalidate();
				toast.success(DocumentFieldLabels.fieldRemoved);
			},
			onError,
		}),
	);

	return (
		<SectionCard title="Campos de documento" dataComponent="system-document-fields">
			<div className="flex flex-col gap-3">
				<p className="text-xs text-muted-foreground">
					Campos disponíveis para inserir em manuais e termos. O valor é preenchido com os dados da
					paciente na hora de gerar o documento.
				</p>

				{fields.data === undefined && <SkeletonRows rows={3} />}

				{fields.data !== undefined && (
					<ul className="flex flex-col divide-y divide-border">
						{fields.data
							.filter((field) => field.source === "patient" || field.source === "system")
							.map((field) => (
								<li key={field.id} className="flex items-center gap-3 py-2">
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-medium text-foreground">{field.label}</p>

										<p className="truncate text-xs text-muted-foreground">
											{field.column === "custom" && DocumentFieldLabels.institutional}
											{field.column !== "custom" && DocumentFieldLabels.fieldColumns[field.column]}
											{field.format !== null && (
												<span className="ml-2 font-mono">{field.format}</span>
											)}
										</p>
									</div>

									<Button
										size="icon"
										variant="ghost"
										className="size-8"
										aria-label={`Renomear ${field.label}`}
										onClick={() => setRenaming({ id: field.id, label: field.label })}
									>
										<Pencil />
									</Button>

									<ConfirmationDialog
										slot={`remove-field-${field.id}`}
										tone="destructive"
										title="O campo será removido."
										pendingLabel="Removendo campo..."
										isPending={remove.isPending && remove.variables.id === field.id}
										onConfirm={() => remove.mutate({ id: field.id })}
										className="inline-flex h-8 items-center rounded-md border border-input bg-background px-2.5 text-xs font-medium shadow-control transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
									>
										Remover
									</ConfirmationDialog>
								</li>
							))}

						{fields.data.length === 0 && (
							<li className="py-2 text-sm text-muted-foreground">Nenhum campo cadastrado.</li>
						)}
					</ul>
				)}

				<div>
					<Button data-slot="create-field" size="sm" onClick={() => setCreating(true)}>
						<Plus />
						Novo campo
					</Button>
				</div>
			</div>

			{creating && <CreateFieldModal onClose={() => setCreating(false)} />}

			{renaming !== null && <RenameFieldModal field={renaming} onClose={() => setRenaming(null)} />}
		</SectionCard>
	);
}

function FieldModal({
	title,
	onClose,
	children,
}: {
	title: string;
	onClose(): void;
	children: React.ReactNode;
}) {
	return (
		<OverlayShell label={title} onClose={onClose} className="items-center justify-center p-4">
			<div className="overlay-panel max-w-md gap-3 p-5">
				<h2 className="type-section-title text-foreground">{title}</h2>

				{children}
			</div>
		</OverlayShell>
	);
}

function CreateFieldModal({ onClose }: { onClose(): void }) {
	const invalidate = useInvalidateFields();
	const {
		control,
		register,
		handleSubmit,
		formState: { isValid },
	} = useForm<
		typeof DocumentFieldFormSchemas.field.inferIn,
		unknown,
		typeof DocumentFieldFormSchemas.field.inferOut
	>({
		resolver: arktypeResolver(DocumentFieldFormSchemas.field),
		mode: "onChange",
		defaultValues: { label: "", column: "name", format: "" },
	});

	const create = useMutation(
		orpc.documents.fields.create.mutationOptions({
			onSuccess: () => {
				invalidate();
				toast.success(DocumentFieldLabels.fieldCreated);
				onClose();
			},
			onError: (error) => toast.error(DocumentFieldLabels.error(errorCode(error))),
		}),
	);

	const submit = handleSubmit((values) => create.mutate(values));

	return (
		<FieldModal title="Novo campo" onClose={onClose}>
			<form className="contents" onSubmit={submit}>
				<Controller
					control={control}
					name="label"
					render={({ field }) => (
						<FormField
							label="Nome"
							value={field.value}
							onChange={field.onChange}
							placeholder="Ex. CPF da paciente"
							autoFocus
							slot="field-label"
						/>
					)}
				/>

				<div className="flex flex-col gap-1.5">
					<Label htmlFor="field-column">Dado da paciente</Label>

					<select
						{...register("column")}
						id="field-column"
						data-slot="field-column"
						className="control-field h-11 md:h-10"
					>
						{Object.entries(DocumentFieldLabels.fieldColumns).map(([value, text]) => (
							<option key={value} value={value}>
								{text}
							</option>
						))}
					</select>
				</div>

				<Controller
					control={control}
					name="format"
					render={({ field }) => (
						<FormField
							label="Formato (opcional)"
							value={field.value}
							onChange={field.onChange}
							placeholder="Ex. ###.###.###-## ou D [de] MMMM [de] YYYY"
							hint="Máscara com # para números de documento, formato de data para datas."
							slot="field-format"
						/>
					)}
				/>

				<div className="mt-1 flex items-center justify-end gap-2">
					<Button type="button" variant="secondary" size="sm" onClick={onClose}>
						Cancelar
					</Button>

					<Button
						variant="secondary"
						size="sm"
						type="submit"
						data-slot="field-submit"
						disabled={!isValid || create.isPending}
					>
						{create.isPending && <Loader2 className="size-4 animate-spin" />}
						Criar campo
					</Button>
				</div>
			</form>
		</FieldModal>
	);
}

function RenameFieldModal({
	field,
	onClose,
}: {
	field: { id: number; label: string };
	onClose(): void;
}) {
	const invalidate = useInvalidateFields();
	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid },
	} = useForm<typeof DocumentFieldFormSchemas.rename.infer>({
		resolver: arktypeResolver(DocumentFieldFormSchemas.rename),
		mode: "onChange",
		defaultValues: { label: field.label },
	});

	const rename = useMutation(
		orpc.documents.fields.rename.mutationOptions({
			onSuccess: () => {
				invalidate();
				toast.success(DocumentFieldLabels.fieldRenamed);
				onClose();
			},
			onError: (error) => toast.error(DocumentFieldLabels.error(errorCode(error))),
		}),
	);

	const submit = handleSubmit(({ label }) => rename.mutate({ id: field.id, label }));

	return (
		<FieldModal title="Renomear campo" onClose={onClose}>
			<form className="contents" onSubmit={submit}>
				<Controller
					control={control}
					name="label"
					render={({ field }) => (
						<FormField
							label="Nome"
							value={field.value}
							onChange={field.onChange}
							autoFocus
							slot="field-label"
						/>
					)}
				/>

				<div className="mt-1 flex items-center justify-end gap-2">
					<Button type="button" variant="secondary" size="sm" onClick={onClose}>
						Cancelar
					</Button>

					<Button
						variant="secondary"
						size="sm"
						type="submit"
						data-slot="field-submit"
						disabled={!isDirty || !isValid || rename.isPending}
					>
						{rename.isPending && <Loader2 className="size-4 animate-spin" />}
						Salvar
					</Button>
				</div>
			</form>
		</FieldModal>
	);
}
